import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import ButtonBase from '@mui/material/ButtonBase'
import FormControlLabel from '@mui/material/FormControlLabel'
import InputAdornment from '@mui/material/InputAdornment'
import LinearProgress from '@mui/material/LinearProgress'
import Link from '@mui/material/Link'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import GroupsIcon from '@mui/icons-material/Groups'
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import SchoolIcon from '@mui/icons-material/School'
import SearchIcon from '@mui/icons-material/Search'
import API from '../../../services/API'
import { GlobalContext, TenantSettingsContext, UserContext } from '../../../App'
import EmptyState from '../../../components/EmptyState/EmptyState'
import PermissionCheck from '../../../helpers/PermissionCheck'
import { downloadPdf } from '../../../helpers/downloadPdf'
import { errorMessage, getMatrix, getMatrixCsv, isOk } from '../../helpers/trainingApi'
import AssignCourseDialog from '../../components/admin/AssignCourseDialog'
import ChaseDialog from '../../components/admin/ChaseDialog'
import MatrixCell from '../../components/admin/MatrixCell'
import MatrixPDF from '../../components/admin/MatrixPDF'
import TimeOnTaskCard from '../../components/admin/TimeOnTaskCard'
import { CELL_STATES, LEGEND_ORDER, STATUS_FILTERS, summarise } from '../../components/admin/matrixStatus'

const NAME_WIDTH = { xs: 132, sm: 220 }
const stickyName = {
  position: 'sticky',
  left: 0,
  width: NAME_WIDTH,
  minWidth: NAME_WIDTH,
  maxWidth: NAME_WIDTH,
  borderRight: '1px solid',
  borderRightColor: 'divider',
}

const matchesStatus = (employee, courses, status) =>
  courses.some((course) => {
    const cell = employee.cells?.[course._id]
    if (!cell?.required) return false
    return status === 'pending' ? !!cell.pending : cell.status === status
  })

const SummaryTile = ({ label, value, caption, state }) => {
  const Icon = state?.Icon
  return (
    <Paper variant="outlined" sx={{ p: 1.5, flex: '1 1 150px', minWidth: 0 }}>
      <Stack direction="row" alignItems="center" gap={0.75}>
        {Icon && <Icon sx={{ color: state.color, fontSize: 18 }} />}
        <Typography variant="caption" color="text.secondary" fontWeight={700}>
          {label}
        </Typography>
      </Stack>
      <Typography variant="h5" fontWeight={700} color="text.primary">
        {value}
      </Typography>
      {caption && (
        <Typography variant="caption" color="text.secondary">
          {caption}
        </Typography>
      )}
    </Paper>
  )
}

const MatrixRow = React.memo(({ employee, courses, onOpen }) => (
  <TableRow hover>
    <TableCell component="th" scope="row" sx={{ ...stickyName, zIndex: 1, bgcolor: 'background.paper', py: 0.5 }}>
      <Link
        component="button"
        type="button"
        underline="hover"
        onClick={() => onOpen(employee)}
        sx={{ fontWeight: 600, textAlign: 'left', fontSize: '0.875rem', color: 'text.primary' }}
      >
        {employee.firstName} {employee.lastName}
      </Link>
      <Typography variant="caption" color="text.secondary" display="block" noWrap>
        {employee.clinicalRole || 'No role set'}
      </Typography>
    </TableCell>
    {courses.map((course) => (
      <TableCell key={course._id} align="center" sx={{ p: 0.25 }}>
        <MatrixCell cell={employee.cells?.[course._id]} course={course} />
      </TableCell>
    ))}
  </TableRow>
))

const TrainingMatrix = () => {
  const navigate = useNavigate()
  const { setToast } = useContext(GlobalContext)
  const { user } = useContext(UserContext)
  const orgSettings = useContext(TenantSettingsContext).orgConfig?.orgSettings

  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(null)

  const [search, setSearch] = useState('')
  const [role, setRole] = useState('')
  const [status, setStatus] = useState('')
  const [gapsOnly, setGapsOnly] = useState(false)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(50)

  const [menu, setMenu] = useState(null) // { anchor, course }
  const [chaseCourse, setChaseCourse] = useState(null)
  const [assignCourseFor, setAssignCourseFor] = useState(null)
  const [exporting, setExporting] = useState(null)

  const load = useCallback(() => {
    getMatrix()
      .then((res) => {
        if (isOk(res)) {
          setData(res.data)
          setLoadError(null)
        } else {
          setLoadError(errorMessage(res, 'Unable to load the training matrix.'))
        }
      })
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const courses = data?.courses ?? []
  const employees = data?.employees ?? []
  const eLearning = !!data?.eLearning

  const roles = useMemo(
    () => [...new Set(employees.map((employee) => employee.clinicalRole).filter(Boolean))].sort(),
    [employees],
  )

  // Role and name narrow who is being looked at (and so the summary);
  // status and gaps then pick out the people needing attention.
  const scoped = useMemo(() => {
    const term = search.trim().toLowerCase()
    return employees.filter(
      (employee) =>
        (!role || employee.clinicalRole === role) &&
        (!term || `${employee.firstName} ${employee.lastName}`.toLowerCase().includes(term)),
    )
  }, [employees, role, search])

  const visible = useMemo(
    () =>
      scoped.filter(
        (employee) =>
          (!gapsOnly || employee.hasExpiredOrMissing) && (!status || matchesStatus(employee, courses, status)),
      ),
    [scoped, gapsOnly, status, courses],
  )

  const summary = useMemo(() => summarise(scoped, courses), [scoped, courses])

  useEffect(() => {
    setPage(0)
  }, [search, role, status, gapsOnly])

  const paged = rowsPerPage > 0 ? visible.slice(page * rowsPerPage, (page + 1) * rowsPerPage) : visible

  // The employee page is addressed by user account id; the matrix rows are
  // employee ids. Until the matrix sends `userAccountId`, look it up.
  const openEmployee = useCallback(
    async (employee) => {
      let userId = employee.userAccountId
      if (!userId) {
        const res = await API.get('/employee-selector', {
          params: { noPhoto: true, searchValue: `${employee.firstName} ${employee.lastName}`.trim() },
          skipGlobalErrorHandler: true,
        })
        userId = (Array.isArray(res.data) ? res.data : []).find(
          (candidate) => String(candidate.employee?._id ?? candidate.employee) === String(employee._id),
        )?._id
      }
      if (userId) navigate(`/employees/${userId}?tab=mandatorytraining`)
      else setToast({ message: 'Unable to open this person’s record.', severity: 'error' })
    },
    [navigate, setToast],
  )

  const filterLabel = [
    role,
    search.trim() ? `name contains "${search.trim()}"` : null,
    STATUS_FILTERS.find((option) => option.value === status)?.label,
    gapsOnly ? 'staff with gaps only' : null,
  ]
    .filter(Boolean)
    .join(', ')

  const exportCsv = async () => {
    setExporting('csv')
    try {
      const res = await getMatrixCsv()
      if (res.status !== 200) {
        setToast({ message: 'Unable to export the training matrix.', severity: 'error' })
        return
      }
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv;charset=utf-8' }))
      const link = window.document.createElement('a')
      link.href = url
      link.download = `training-matrix-${moment().format('YYYY-MM-DD')}.csv`
      window.document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
    } finally {
      setExporting(null)
    }
  }

  const exportPdf = async () => {
    setExporting('pdf')
    try {
      await downloadPdf(
        <MatrixPDF
          orgSettings={orgSettings}
          courses={courses}
          employees={visible}
          summary={summarise(visible, courses)}
          filterLabel={filterLabel}
          printedBy={`Generated by ${user?.firstName ?? ''} ${user?.lastName ?? ''} on ${moment().format('D MMMM YYYY, HH:mm')}`}
        />,
        `training-matrix-${moment().format('YYYY-MM-DD')}.pdf`,
      )
    } catch (e) {
      console.error(e)
      setToast({ message: 'Unable to generate the PDF.', severity: 'error' })
    } finally {
      setExporting(null)
    }
  }

  const body = () => {
    if (loading) return <LinearProgress />
    if (loadError) return <Alert severity="error">{loadError}</Alert>

    if (courses.length === 0) {
      return (
        <EmptyState
          icon={SchoolIcon}
          title="No mandatory training courses configured"
          description="This matrix tracks every member of staff against the courses their role requires, but no courses have been set up yet."
          steps={[
            'Open Site Configuration → Employees → Mandatory Training.',
            'Add each course your organisation requires, with its renewal frequency.',
            'Choose which roles each course applies to.',
            'Staff then see their courses under My Training, and compliance appears here automatically.',
          ]}
          actions={[{ label: 'Configure Courses', to: '/site-configuration/mandatory-training' }]}
        />
      )
    }

    if (employees.length === 0) {
      return (
        <EmptyState
          icon={GroupsIcon}
          title="No active staff"
          description="The matrix lists every member of staff with the status New Starter or Live. There are none yet."
          actions={[{ label: 'Open Employees', to: '/employees' }]}
        />
      )
    }

    return (
      <>
        <Stack direction="row" gap={1.5} flexWrap="wrap">
          <SummaryTile
            label="Compliance"
            value={summary.percent === null ? 'n/a' : `${summary.percent}%`}
            caption={
              summary.required
                ? `${summary.compliant + summary.expiring} of ${summary.required} required courses in date`
                : 'No courses are required for these staff'
            }
          />
          <SummaryTile label="Overdue" value={summary.overdue} state={CELL_STATES.overdue} />
          <SummaryTile label="Expiring soon" value={summary.expiring} state={CELL_STATES.expiring} caption="Within 6 weeks" />
          <SummaryTile label="Not completed" value={summary.notCompleted} state={CELL_STATES.notCompleted} />
          {summary.pending > 0 && (
            <SummaryTile label="Awaiting approval" value={summary.pending} state={CELL_STATES.pending} />
          )}
        </Stack>

        <Stack direction={{ xs: 'column', md: 'row' }} gap={1.5} alignItems={{ md: 'center' }} flexWrap="wrap">
          <TextField
            size="small"
            label="Search by name"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: { md: 220 } }}
          />
          <TextField
            select
            size="small"
            label="Role"
            value={role}
            onChange={(event) => setRole(event.target.value)}
            sx={{ minWidth: { md: 180 } }}
          >
            <MenuItem value="">All roles</MenuItem>
            {roles.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Status"
            value={status}
            onChange={(event) => setStatus(event.target.value)}
            sx={{ minWidth: { md: 190 } }}
          >
            <MenuItem value="">Any status</MenuItem>
            {STATUS_FILTERS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <FormControlLabel
            control={<Switch checked={gapsOnly} onChange={(event) => setGapsOnly(event.target.checked)} />}
            label="Only show staff with gaps"
          />
        </Stack>

        <Stack direction="row" columnGap={2} rowGap={0.5} flexWrap="wrap" aria-label="Key">
          {LEGEND_ORDER.map((key) => {
            const { Icon, label, color } = CELL_STATES[key]
            return (
              <Stack key={key} direction="row" alignItems="center" gap={0.5}>
                <Icon sx={{ color, fontSize: 16 }} />
                <Typography variant="caption">{label}</Typography>
              </Stack>
            )
          })}
          <Stack direction="row" alignItems="center" gap={0.5}>
            <SchoolIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
            <Typography variant="caption">Completed by e-learning</Typography>
          </Stack>
        </Stack>

        <Paper variant="outlined">
          <TableContainer sx={{ maxHeight: { xs: '70vh', md: 'calc(100vh - 220px)' }, minHeight: 240 }}>
            <Table stickyHeader size="small" aria-label="Training matrix">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{ ...stickyName, zIndex: 4, bgcolor: 'primary.main', color: 'primary.contrastText', fontWeight: 600 }}
                  >
                    Staff ({visible.length})
                  </TableCell>
                  {courses.map((course) => (
                    <TableCell
                      key={course._id}
                      align="center"
                      sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', p: 0, minWidth: 104, maxWidth: 140 }}
                    >
                      <Tooltip title={`${course.title}: actions`}>
                        <ButtonBase
                          onClick={(event) => setMenu({ anchor: event.currentTarget, course })}
                          aria-haspopup="menu"
                          aria-label={`${course.title}${course.eLearning && eLearning ? ', e-learning' : ''}: course actions`}
                          sx={{
                            width: '100%',
                            minHeight: 64,
                            px: 0.75,
                            py: 0.5,
                            gap: 0.25,
                            color: 'inherit',
                            '&:focus-visible': { outline: '2px solid #FFFFFF', outlineOffset: -3 },
                          }}
                        >
                          <Typography
                            component="span"
                            sx={{
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              lineHeight: 1.2,
                              display: '-webkit-box',
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {course.title}
                          </Typography>
                          <Stack alignItems="center" flexShrink={0}>
                            {eLearning && course.eLearning && <SchoolIcon sx={{ fontSize: 14 }} />}
                            <ArrowDropDownIcon sx={{ fontSize: 18 }} />
                          </Stack>
                        </ButtonBase>
                      </Tooltip>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paged.map((employee) => (
                  <MatrixRow key={employee._id} employee={employee} courses={courses} onOpen={openEmployee} />
                ))}
                {visible.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={courses.length + 1}>
                      <Typography color="text.secondary" py={2}>
                        No staff match these filters.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={visible.length}
            page={page}
            onPageChange={(event, value) => setPage(value)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10))
              setPage(0)
            }}
            rowsPerPageOptions={[25, 50, 100, { label: 'All', value: -1 }]}
            labelRowsPerPage="Staff per page"
          />
        </Paper>

        {eLearning && <TimeOnTaskCard />}
      </>
    )
  }

  const canExport = !loading && courses.length > 0 && employees.length > 0

  return (
    <PermissionCheck roles={['Personnel', 'Governance']} showAlert>
      <Stack gap={2}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          gap={1}
        >
          <Box>
            <Typography variant="title2" component="h1">
              Training Matrix
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Every active member of staff against the training their role requires. Select a
              course heading to remind staff{eLearning ? ' or assign e-learning' : ''}.
            </Typography>
          </Box>
          {canExport && (
            <Stack direction="row" gap={1} flexShrink={0}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<FileDownloadIcon />}
                onClick={exportCsv}
                disabled={!!exporting}
              >
                {exporting === 'csv' ? 'Exporting...' : 'Export CSV'}
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<PictureAsPdfIcon />}
                onClick={exportPdf}
                disabled={!!exporting || visible.length === 0}
              >
                {exporting === 'pdf' ? 'Generating...' : 'Export PDF'}
              </Button>
            </Stack>
          )}
        </Stack>

        {body()}

        <Menu open={!!menu} anchorEl={menu?.anchor} onClose={() => setMenu(null)}>
          <MenuItem
            onClick={() => {
              setChaseCourse(menu.course)
              setMenu(null)
            }}
          >
            <ListItemIcon>
              <NotificationsActiveIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Remind staff who are overdue or not complete</ListItemText>
          </MenuItem>
          {eLearning && menu?.course?.eLearning && (
            <MenuItem
              onClick={() => {
                setAssignCourseFor(menu.course)
                setMenu(null)
              }}
            >
              <ListItemIcon>
                <PersonAddAlt1Icon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Assign to a member of staff</ListItemText>
            </MenuItem>
          )}
        </Menu>

        <ChaseDialog
          open={!!chaseCourse}
          onClose={() => setChaseCourse(null)}
          course={chaseCourse}
          employees={employees}
        />
        <AssignCourseDialog
          open={!!assignCourseFor}
          onClose={() => setAssignCourseFor(null)}
          course={assignCourseFor}
          employees={employees}
          onAssigned={(employee) => {
            setToast({ message: `${assignCourseFor.title} assigned to ${employee.firstName} ${employee.lastName}` })
            setAssignCourseFor(null)
            load()
          }}
        />
      </Stack>
    </PermissionCheck>
  )
}

export default TrainingMatrix
